var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useCallback, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useFormContext } from 'react-hook-form';
import { useGetSuggestedMeetingTimesLazyQuery, } from '../../../../generated/graphql';
import { useEventComposerGlobalContext } from '../../../composers/EventComposerGlobalContext';
import { useConfig } from '../../../context/ConfigContext';
import { groupSuggestions } from './mapSuggestions';
import { buildPartialInfo, extractErrorCode, } from './suggestionsOutcome';
/**
 * Builds the {@link SuggestedMeetingTimesInput} from the current composer form
 * plus context, runs `getSuggestedMeetingTimes` on demand, and interprets the
 * response into a single {@link SuggestionsOutcome} the drawer can branch on.
 *
 * Create mode only — `editContext` is never sent. The query is real-time and
 * uncached, so it's exposed as an explicit trigger (`fetchSuggestions`) rather
 * than firing on every form change.
 */
export var useSuggestedMeetingTimes = function () {
    var _a, _b;
    var getValues = useFormContext().getValues;
    var _c = useEventComposerGlobalContext(), timezone = _c.timezone, timeFormat = _c.timeFormat;
    var userContext = useConfig().userContext;
    var organizerEmail = (_b = (_a = userContext.currentUser.primaryEmail) === null || _a === void 0 ? void 0 : _a.email) !== null && _b !== void 0 ? _b : '';
    var _d = __read(useGetSuggestedMeetingTimesLazyQuery(), 2), fetch = _d[0], result = _d[1];
    // The window we last asked for — used to detect server-side clamping.
    var _e = __read(useState(null), 2), requestedWindow = _e[0], setRequestedWindow = _e[1];
    /**
     * Reads the latest form values at call time (avoids stale closures) and
     * assembles the query input. Returns null when required times are missing.
     */
    var buildInput = useCallback(function () {
        var _a = getValues(), invitees = _a.invitees, startTime = _a.startTime, endTime = _a.endTime, date = _a.date, isAllDayEvent = _a.isAllDayEvent, recurrenceRule = _a.recurrenceRule;
        if (!startTime || !endTime || !date) {
            return null;
        }
        // The organizer is the HARD constraint. The server de-dupes, but strip the
        // organizer out of the soft set here too and drop invitees without emails.
        var inviteeEmails = (invitees !== null && invitees !== void 0 ? invitees : [])
            .map(function (invitee) { return invitee.email; })
            .filter(function (email) {
            return !!email && email.toLowerCase() !== organizerEmail.toLowerCase();
        });
        var durationMinutes = endTime.diff(startTime, 'minutes');
        // Search window: from = the user's selected start time; to = the end of the
        // following day. Both presented in the event timezone, kept within the
        // server's 2-day clamp.
        var from = startTime.clone().tz(timezone).format();
        var to = moment(date)
            .clone()
            .tz(timezone)
            .add(1, 'day')
            .endOf('day')
            .format();
        return {
            editContext: null,
            organizerEmail: organizerEmail,
            inviteeEmails: inviteeEmails,
            durationMinutes: durationMinutes,
            timeZone: timezone,
            searchWindow: { from: from, to: to },
            draftStartTime: from,
            isAllDay: !!isAllDayEvent,
            isRecurring: !!recurrenceRule,
        };
    }, [getValues, organizerEmail, timezone]);
    var fetchSuggestions = useCallback(function () {
        var input = buildInput();
        if (!input) {
            return;
        }
        setRequestedWindow(input.searchWindow);
        return fetch({ variables: { input: input } });
    }, [buildInput, fetch]);
    var outcome = useMemo(function () {
        var _a;
        var data = (_a = result.data) === null || _a === void 0 ? void 0 : _a.getSuggestedMeetingTimes;
        if (result.error) {
            return { status: 'error', code: extractErrorCode(result.error) };
        }
        // Only collapse to a bare loading/idle state when there's nothing to show.
        // During a refetch Apollo keeps the previous `data`, so we keep rendering
        // it (avoids the prompt/list flickering back to the button on every
        // invitee or date/time change).
        if (!data) {
            return result.loading && result.called
                ? { status: 'loading' }
                : { status: 'idle' };
        }
        var partial = buildPartialInfo(data, requestedWindow, organizerEmail);
        // Branch order per the integration doc: skipped first (intentional, not an
        // error), then success, then the empty edge case. Partial rides along.
        if (data.skippedReason) {
            return { status: 'skipped', reason: data.skippedReason, partial: partial };
        }
        if (data.suggestions.length > 0) {
            var _b = groupSuggestions(data.suggestions, timeFormat), available = _b.available, conflicts = _b.conflicts;
            return { status: 'success', available: available, conflicts: conflicts, partial: partial };
        }
        return { status: 'empty', partial: partial };
    }, [result, requestedWindow, organizerEmail, timeFormat]);
    return {
        fetchSuggestions: fetchSuggestions,
        outcome: outcome,
        loading: result.loading,
        called: result.called,
    };
};
